import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { API_URL, getAuthToken } from "@/lib/api";
import { getLocalizedErrorMessage } from "@/lib/error-i18n";

const API_BASE_URL = API_URL;

// API request helper
const apiRequest = async (endpoint: string, options: RequestInit = {}) => {
  const token = getAuthToken();
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || "Request failed");
  }

  return data;
};

// Types
export interface QuizAnswer {
  questionIndex: number;
  selectedAnswer: string;
}

export interface QuizSubmitPayload {
  answers: QuizAnswer[];
  timeSpent?: number;
}

export interface QuizAttemptResult {
  attemptId: string;
  attemptNumber: number;
  score: number;
  correctAnswers: number;
  totalQuestions: number;
  passed: boolean;
  passingScore: number;
  strengthName: string;
  answers: Array<{
    questionIndex: number;
    question: string;
    selectedAnswer: string;
    correctAnswer: string;
    isCorrect: boolean;
    explanation: string;
  }>;
}

export interface QuizAttempt {
  _id: string;
  attemptNumber: number;
  score: number;
  correctAnswers: number;
  totalQuestions: number;
  passed: boolean;
  timeSpent: number;
  completedAt: string;
  answers?: Array<{
    questionIndex: number;
    question: string;
    selectedAnswer: string;
    correctAnswer: string;
    isCorrect: boolean;
    explanation: string;
  }>;
}

export interface BestScore {
  attemptNumber: number;
  score: number;
  correctAnswers: number;
  totalQuestions: number;
  passed: boolean;
  completedAt: string;
  strengthName: string;
}

export interface ContentProgress {
  contentId: string;
  strengthId: string;
  strengthName: string;
  strengthNameAr: string;
  domain: string;
  shortDescription: string;
  totalAttempts: number;
  bestScore: number;
  passed: boolean;
  lastAttemptDate: string;
}

export interface QuizProgressSummary {
  totalContentAttempted: number;
  totalContentPassed: number;
  totalAttempts: number;
  averageScore: number;
  passRate: number;
}

export interface QuizProgress {
  summary: QuizProgressSummary;
  contentProgress: ContentProgress[];
}

// ============================================
// Custom Hooks
// ============================================

/**
 * Submit quiz answers
 */
export const useSubmitQuiz = (contentId: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: QuizSubmitPayload) => {
      const response = await apiRequest(`/content/${contentId}/quiz/submit`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      return response.data as QuizAttemptResult;
    },
    onSuccess: (data) => {
      // Invalidate related queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: ["quizAttempts", contentId] });
      queryClient.invalidateQueries({ queryKey: ["bestScore", contentId] });
      queryClient.invalidateQueries({ queryKey: ["quizProgress"] });

      if (data.passed) {
        toast({
          title: "Quiz Passed! 🎉",
          description: `Congratulations! You scored ${data.score}%`,
          className: "bg-green-500/90 text-white border-green-600",
        });
      } else {
        toast({
          title: "Keep Going! 💪",
          description: `You scored ${data.score}%. You can retake the quiz to improve your score!`,
          className: "bg-yellow-500/90 text-white border-yellow-600",
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Submission Failed",
        description: getLocalizedErrorMessage(error, "errors.requestFailed"),
        variant: "destructive",
      });
    },
  });
};

/**
 * Get quiz attempts history for specific content
 */
export const useQuizAttempts = (contentId: string, enabled: boolean = true) => {
  return useQuery<QuizAttempt[]>({
    queryKey: ["quizAttempts", contentId],
    queryFn: async () => {
      const response = await apiRequest(`/content/${contentId}/quiz/attempts`);
      return response.data || [];
    },
    enabled: enabled && !!contentId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Get best quiz score for specific content
 */
export const useBestScore = (contentId: string, enabled: boolean = true) => {
  return useQuery<BestScore | null>({
    queryKey: ["bestScore", contentId],
    queryFn: async () => {
      try {
        const response = await apiRequest(`/content/${contentId}/quiz/best-score`);
        return response.data;
      } catch (error: any) {
        // Return null if no attempts found (404)
        if (error.message?.includes("No quiz attempts")) {
          return null;
        }
        throw error;
      }
    },
    enabled: enabled && !!contentId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: (failureCount, error: any) => {
      // Don't retry on 404
      if (error.message?.includes("No quiz attempts")) {
        return false;
      }
      return failureCount < 2;
    },
  });
};

/**
 * Get overall quiz progress for current user
 */
export const useQuizProgress = (enabled: boolean = true) => {
  return useQuery<QuizProgress>({
    queryKey: ["quizProgress"],
    queryFn: async () => {
      const response = await apiRequest("/content/quiz/my-progress");
      return response.data;
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Get quiz statistics (Admin only)
 */
export const useQuizStats = (enabled: boolean = false) => {
  return useQuery({
    queryKey: ["quizStats"],
    queryFn: async () => {
      const response = await apiRequest("/content/quiz/stats");
      return response.data;
    },
    enabled,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Prefetch quiz data for better UX
 */
export const usePrefetchQuizData = () => {
  const queryClient = useQueryClient();

  const prefetchBestScore = (contentId: string) => {
    queryClient.prefetchQuery({
      queryKey: ["bestScore", contentId],
      queryFn: async () => {
        try {
          const response = await apiRequest(`/content/${contentId}/quiz/best-score`);
          return response.data;
        } catch {
          return null;
        }
      },
      staleTime: 2 * 60 * 1000,
    });
  };

  const prefetchAttempts = (contentId: string) => {
    queryClient.prefetchQuery({
      queryKey: ["quizAttempts", contentId],
      queryFn: async () => {
        const response = await apiRequest(`/content/${contentId}/quiz/attempts`);
        return response.data || [];
      },
      staleTime: 2 * 60 * 1000,
    });
  };

  return { prefetchBestScore, prefetchAttempts };
};
